import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import AuthContext from "../../context/AuthContext";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  formatSsoErrorMessage,
  getSsoErrorInfo,
  SsoErrorInfo,
} from "../../utils/ssoError";
import { refreshAccessToken } from "../../utils/session";

export const SsoCallbackPage = () => {
  const navigate = useNavigate();
  const { setToken, fetchUser } = useContext(AuthContext);
  const [ssoError, setSsoError] = React.useState<SsoErrorInfo | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const errorInfo = getSsoErrorInfo(params);

    const finishWith = (accessToken: string) => {
      if (typeof setToken === "function") setToken(accessToken);
      if (typeof fetchUser === "function") fetchUser(accessToken);
      navigate("/", { replace: true });
    };

    // Preferred path: the server set the refresh cookie during the SSO redirect, so no
    // credential has to travel in the URL. The ?token= fallback is for servers that
    // still send it.
    if (!errorInfo) {
      let cancelled = false;
      refreshAccessToken().then((result) => {
        if (cancelled) return;
        if (result.status === "ok") return finishWith(result.token);
        if (token) return finishWith(token);
        navigate("/", {
          replace: true,
          state: {
            error:
              "Microsoft SSO did not return a sign-in token. Please try again or contact an administrator.",
          },
        });
      });
      return () => {
        cancelled = true;
      };
    }

    if (errorInfo) {
      setSsoError(errorInfo);
      sessionStorage.setItem("ssoError", formatSsoErrorMessage(errorInfo));

      const redirectTimer = window.setTimeout(() => {
        navigate("/", {
          replace: true,
          state: { error: formatSsoErrorMessage(errorInfo) },
        });
      }, 9000);

      return () => window.clearTimeout(redirectTimer);
    }

    navigate("/", {
      replace: true,
      state: {
        error:
          "Microsoft SSO did not return a sign-in token. Please try again or contact an administrator.",
      },
    });
  }, [navigate, setToken, fetchUser]);

  const handleReturnToLogin = () => {
    navigate("/", {
      replace: true,
      state: ssoError ? { error: formatSsoErrorMessage(ssoError) } : undefined,
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      {ssoError ? (
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: 560,
            p: 4,
            borderRadius: 3,
          }}
        >
          <Alert severity="error" variant="filled" sx={{ mb: 3 }}>
            {ssoError.title}
          </Alert>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Microsoft SSO could not complete
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {ssoError.message}
          </Typography>
          {ssoError.detail && (
            <Typography
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                bgcolor: "grey.100",
                borderRadius: 2,
                p: 2,
                mb: 3,
                fontSize: "0.875rem",
              }}
            >
              {ssoError.detail}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You will be returned to the sign-in page automatically.
          </Typography>
          <Button variant="contained" onClick={handleReturnToLogin}>
            Back to sign in
          </Button>
        </Paper>
      ) : (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing in with Microsoft...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
